import { useMsal } from "@azure/msal-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/OL_LOGO.png";
import AuthButton from "./AuthButton";
import { useI18n } from "../i18n";
import { getAccountRoles, isAdminAccount } from "../utils/accountRoles";
import "./Header.css";

const THEME_STORAGE_KEY = "olt_front_theme";
const HEALTH_INTERVAL_MS = 5_000;

function resolveBackendUrl() {
  if (typeof window === "undefined") return "";
  const env = window.location.hostname === "localhost"
    ? (process.env.REACT_APP_API_URL_LOCAL || process.env.REACT_APP_API_URL)
    : (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL_LOCAL);
  return (env || "").replace(/^["']|["']$/g, "").trim();
}

function useBackendHealth() {
  const [status, setStatus] = useState("unknown");
  const [latency, setLatency] = useState(null);
  const [tick, setTick] = useState(0);

  const ping = useCallback(async () => {
    const url = resolveBackendUrl();
    if (!url) { setStatus("unknown"); return; }
    const start = performance.now();
    try {
      const res = await fetch(`${url}/health`, { method: "GET", signal: AbortSignal.timeout(8000) });
      const ms = Math.round(performance.now() - start);
      setLatency(ms);
      setStatus(res.ok ? "up" : "down");
    } catch {
      setLatency(null);
      setStatus("down");
    }
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    ping();
    const id = setInterval(ping, HEALTH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [ping]);

  return { status, latency, tick };
}

function resolveInitialTheme() {
  if (typeof window === "undefined") return "light";
  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (storedTheme === "dark" || storedTheme === "light") return storedTheme;
  return "light";
}

const Header = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useI18n();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [theme, setTheme] = useState(resolveInitialTheme);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleLogoClick = () => {
    navigate("/");
    setIsMobileMenuOpen(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await instance.clearCache();
      await instance.logoutPopup({ mainWindowRedirectUri: window.location.origin });
      setIsUserMenuOpen(false);
      setIsMobileMenuOpen(false);
      navigate("/");
    } catch (e) {
      console.error("Erreur de déconnexion:", e);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && !event.target.closest(".burger-button")) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.setAttribute("data-theme", theme);
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const userRoles = getAccountRoles(accounts[0]).join(", ") || t("no_role");
  const isAdmin = isAdminAccount(accounts[0]);
  const { status: backendStatus, latency: backendLatency, tick: pingTick } = useBackendHealth();
  const healthMsRef = useRef(null);

  useEffect(() => {
    const el = healthMsRef.current;
    if (!el) return;
    el.classList.remove("health-ms-flash");
    void el.offsetWidth;
    el.classList.add("health-ms-flash");
  }, [pingTick]);

  const navButtons = (
    <>
      <button
        className={`top-nav-button ${location.pathname === "/" ? "top-nav-active" : ""}`}
        onClick={() => handleNavigate("/")}
      >
        {t("nav_home")}
      </button>
      <button
        className={`top-nav-button ${location.pathname === "/storage" ? "top-nav-active" : ""}`}
        onClick={() => handleNavigate("/storage")}
      >
        {t("nav_storage")}
      </button>
      {isAdmin && (
        <button
          className={`top-nav-button top-nav-admin ${location.pathname === "/admin" ? "top-nav-active" : ""}`}
          onClick={() => handleNavigate("/admin")}
        >
          {t("nav_admin")}
        </button>
      )}
      {isAdmin && (
        <button
          className={`top-nav-button top-nav-admin ${location.pathname === "/coordinator" ? "top-nav-active" : ""}`}
          onClick={() => handleNavigate("/coordinator")}
        >
          {t("nav_coordinator")}
        </button>
      )}
      {isAdmin && (
        <button
          className={`top-nav-button top-nav-admin ${location.pathname === "/templates" ? "top-nav-active" : ""}`}
          onClick={() => handleNavigate("/templates")}
        >
          {t("nav_templates")}
        </button>
      )}
    </>
  );

  const healthButton = (
    <div
      className={`health-indicator health-${backendStatus}`}
      title={
        backendStatus === "up"
          ? `Backend OK — ${backendLatency}ms`
          : backendStatus === "down"
            ? "Backend inaccessible"
            : "Vérification..."
      }
    >
      <span className="health-dot" />
      {backendStatus === "up" && backendLatency != null && (
        <span className="health-ms" ref={healthMsRef}>{backendLatency}ms</span>
      )}
    </div>
  );

  return (
    <header className="header">
      <div className="logo-container" onClick={handleLogoClick}>
        <img src={logo} alt="Company Logo" className="logo" />
        <div className="logo-text">
          <p className="logo-text_name">OPEN LAKE TECHNOLOGY</p>
          <p className="logo-text_slogan">Communication Compliance, Monitoring and Adoption</p>
        </div>
      </div>

      {/* Desktop nav */}
      {accounts.length > 0 && (
        <div className="header-center-nav desktop-only">
          {navButtons}
        </div>
      )}

      <div className="header-right">
        <div className="desktop-only header-right-controls">
          {healthButton}
          <button
            className="lang-toggle-button"
            onClick={() => setLanguage(language === "fr" ? "en" : "fr")}
            aria-label={language === "fr" ? t("language_en") : t("language_fr")}
          >
            {language === "fr" ? "EN" : "FR"}
          </button>
          <button
            className="theme-toggle-button"
            onClick={() => setTheme((prev) => (prev === "dark" ? "light" : "dark"))}
            aria-label={theme === "dark" ? t("light_mode") : t("dark_mode")}
          >
            <span className={`theme-icon ${theme === "dark" ? "theme-icon-sun" : "theme-icon-moon"}`} aria-hidden="true">
              {theme === "dark" ? "☀" : "☾"}
            </span>
          </button>
          <div className="user-menu" ref={userMenuRef}>
            <button
              className="user-avatar-button"
              onClick={() => setIsUserMenuOpen((prev) => !prev)}
              title={t("user_menu")}
            >
              <svg className="user-avatar-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.5 4.5 0 0 0 12 12Zm0 2.25c-3.76 0-6.75 2.1-6.75 4.5v.75h13.5v-.75c0-2.4-2.99-4.5-6.75-4.5Z" />
              </svg>
            </button>
            {isUserMenuOpen && (
              <div className="user-dropdown">
                {accounts.length > 0 ? (
                  <>
                    <div className="user-name">{accounts[0].name}</div>
                    <div className="user-email">{accounts[0].username}</div>
                    <div className="user-roles">{t("company_label")} : {userRoles}</div>
                    <button onClick={handleLogout} className="logout-button dropdown-logout">
                      {t("logout")}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="user-name">{t("not_connected")}</div>
                    <div className="user-email">{t("please_login")}</div>
                    <div className="header-login-action">
                      <AuthButton />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {accounts.length > 0 && (
          <button
            className="burger-button mobile-only"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-label="Menu"
          >
            <span className={`burger-icon ${isMobileMenuOpen ? "burger-icon-open" : ""}`}>
              <span />
              <span />
              <span />
            </span>
          </button>
        )}
      </div>

      {/* Mobile slide-down panel */}
      {isMobileMenuOpen && (
        <div className="mobile-menu" ref={mobileMenuRef}>
          <nav className="mobile-nav">
            {navButtons}
          </nav>
          <div className="mobile-controls">
            <div className="user-menu" ref={userMenuRef}>
              <button
                className="user-avatar-button"
                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                title={t("user_menu")}
              >
                <svg className="user-avatar-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.5 4.5 0 0 0 12 12Zm0 2.25c-3.76 0-6.75 2.1-6.75 4.5v.75h13.5v-.75c0-2.4-2.99-4.5-6.75-4.5Z" />
                </svg>
              </button>
              {isUserMenuOpen && (
                <div className="user-dropdown">
                  {accounts.length > 0 ? (
                    <>
                      <div className="user-name">{accounts[0].name}</div>
                      <div className="user-email">{accounts[0].username}</div>
                      <div className="user-roles">{t("company_label")} : {userRoles}</div>
                      <button onClick={handleLogout} className="logout-button dropdown-logout">
                        {t("logout")}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="user-name">{t("not_connected")}</div>
                      <div className="user-email">{t("please_login")}</div>
                      <div className="header-login-action">
                        <AuthButton />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <button
              className="lang-toggle-button"
              onClick={() => setLanguage(language === "fr" ? "en" : "fr")}
            >
              {language === "fr" ? "EN" : "FR"}
            </button>
            <button
              className="theme-toggle-button"
              onClick={() => setTheme((prev) => (prev === "dark" ? "light" : "dark"))}
            >
              <span className={`theme-icon ${theme === "dark" ? "theme-icon-sun" : "theme-icon-moon"}`} aria-hidden="true">
                {theme === "dark" ? "☀" : "☾"}
              </span>
            </button>
            <span className="mobile-controls-spacer" />
            {healthButton}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
